// =============================================================================
// domain/defaultRisk.ts — editing the per-instrument default risk map.
// -----------------------------------------------------------------------------
// PORTED FROM `tradecoach-saas/app/index.html` — semantics, not code (D3):
// `renderDefaultRiskTable`'s change handlers, `#btn-dri-add`, `#btn-dri-clear`.
//
// ⚠ THIS SETTING IS ALREADY BEING READ, AND HAS BEEN SINCE THE TRADE FORM
// SHIPPED. `defaultRiskFor` fills Risk and R:R on every trade opened without a
// stored plan, and Realized R is `points ÷ risk`, so an unset instrument blanks
// both R columns on the form. Josh's screenshot of beta showed `Planned R 2.00 /
// Realized R 3.42` where the port showed em dashes on the same trade — the
// difference was this map, which the port could read and not edit.
//
// ── ⚠ THE ENTRY IS MERGED, NEVER REPLACED ──
//
// Each entry is a JSON object and this editor renders exactly two of its fields.
// The old client's onboarding writes the same entries, and a future version may
// add more. Every function below SPREADS the existing entry, so a key this
// screen has never heard of survives an edit.
//
// That is D62 one level down: never own a field you will not write back. The
// field here is not a column, it is a key inside a blob — and the failure is
// identical, a silent delete on save.
//
// ── ⚠ NO WALL CLOCK ──
//
// `updatedAt` is stamped from an injected `now`, per D26. These are pure
// functions; the caller supplies the time.
// =============================================================================

/** One instrument's stored default. Extra keys are preserved, never typed away. */
export type DefaultRiskEntry = Record<string, unknown>;
export type DefaultRiskMap = Record<string, DefaultRiskEntry>;

/** The two fields this editor owns. */
export type DefaultRiskField = 'riskPoints' | 'rrPlanned';

/** Normalise whatever the preferences layer handed us into a map. */
export function asRiskMap(v: unknown): DefaultRiskMap {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
  const out: DefaultRiskMap = {};
  for (const [k, entry] of Object.entries(v as Record<string, unknown>)) {
    out[k] = (entry && typeof entry === 'object' && !Array.isArray(entry))
      ? { ...(entry as Record<string, unknown>) }
      : {};
  }
  return out;
}

/** Configured symbols, sorted — the table's row order. */
export function riskSymbols(map: DefaultRiskMap): string[] {
  return Object.keys(map).sort();
}

/**
 * Set or clear one field on one instrument.
 *
 * ⚠ A value that is not a FINITE NUMBER GREATER THAN ZERO deletes the key
 * rather than storing it. That is the old client's rule and it is load-bearing
 * in both directions:
 *
 *   • `0` or a blank box must not persist. `defaultRiskFor` rejects
 *     `riskPoints <= 0` on read, so a stored 0 is a value that can never be
 *     used — an entry that looks configured on this screen and does nothing on
 *     every other one.
 *   • deleting the KEY (not the entry) is what lets an instrument keep a
 *     configured R:R while its stop is cleared.
 */
export function setRiskField(
  map: DefaultRiskMap, symbol: string, field: DefaultRiskField,
  raw: string | number | null | undefined, now: string,
): DefaultRiskMap {
  const next: DefaultRiskMap = { ...map };
  const entry: DefaultRiskEntry = { ...(next[symbol] ?? {}) };
  const val = typeof raw === 'number' ? raw : parseFloat(String(raw ?? '').trim());

  if (Number.isFinite(val) && val > 0) entry[field] = val;
  else delete entry[field];

  entry['updatedAt'] = now;
  next[symbol] = entry;
  return next;
}

/**
 * Add an instrument with both fields unset.
 *
 * ⚠ An EXISTING entry is left completely alone — no re-stamp of `updatedAt`,
 * no reset of its values. Beta guards the same way (`if (!map[sym])`), and
 * without it "add" on an already-configured symbol silently wipes a stop the
 * user set months ago.
 */
export function addRiskInstrument(
  map: DefaultRiskMap, symbol: string, now: string,
): DefaultRiskMap {
  if (!symbol || map[symbol]) return map;
  return { ...map, [symbol]: { riskPoints: null, rrPlanned: null, updatedAt: now } };
}

/** Remove an instrument entirely. */
export function removeRiskInstrument(map: DefaultRiskMap, symbol: string): DefaultRiskMap {
  if (!(symbol in map)) return map;
  const next = { ...map };
  delete next[symbol];
  return next;
}

/**
 * The risk in dollars for one contract, or null when it cannot be stated.
 *
 * ⚠ Null rather than 0 when the stop is unset or the contract's $/point is
 * unknown. A zero here reads as "this trade risks nothing".
 */
export function riskDollars(
  entry: DefaultRiskEntry | undefined, dollarsPerPoint: number | null | undefined,
): number | null {
  const pts = Number(entry?.['riskPoints']);
  const dpp = Number(dollarsPerPoint);
  if (!Number.isFinite(pts) || pts <= 0) return null;
  if (!Number.isFinite(dpp) || dpp <= 0) return null;
  return pts * dpp;
}

/** Instruments not yet configured — what the add dropdown offers. */
export function unconfiguredSymbols(
  map: DefaultRiskMap, catalogue: readonly { symbol: string }[] | null | undefined,
): string[] {
  const configured = new Set(Object.keys(map));
  return (catalogue ?? []).map((c) => c.symbol).filter((s) => s && !configured.has(s));
}
